import { useMemo, useState } from 'react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { formatRevenueFull } from './formatters'
import { EntryCells, GRID_TEMPLATE, HeaderCell, NewProductRow, OtherRow, ParentRow } from './NodeSearchRows'

export interface NodeRevenueEntry {
  product_node_id: string
  name: string
  has_children: boolean
  revenue: string
  share: number
}

export interface NodeRevenueData {
  period_start: string
  period_end: string
  total_revenue: string
  entries: NodeRevenueEntry[]
  drill: {
    product_node_id: string
    name: string
    path: string
    scope_revenue: string
    parent_share: number
  } | null
}

export type AddedKey = string

interface Props {
  data: NodeRevenueData
  addedKeys: Set<AddedKey>
  currency?: string | null
  specialAdded?: boolean
  newProductAdded?: boolean
  onAdd: (entry: NodeRevenueEntry) => void
  onAddSpecial?: () => void
  onAddNewProduct?: () => void
  onClearDrill: () => void
}

export default function NodeSearchPanel({
  data,
  addedKeys,
  currency,
  specialAdded = false,
  newProductAdded = false,
  onAdd,
  onAddSpecial,
  onAddNewProduct,
  onClearDrill
}: Props) {
  const [query, setQuery] = useState('')
  const drill = data.drill
  const items = data.entries
  const querying = query.trim().length > 0

  const list = useMemo(() => {
    if (!querying) return items
    const q = query.toLowerCase()
    return items.filter((e) => e.name.toLowerCase().includes(q))
  }, [query, querying, items])

  return (
    <div
      className="border rounded-lg flex flex-col"
      style={{ borderColor: 'var(--border)', background: 'var(--background)', height: '70vh' }}
    >
      <div className="p-3 border-b" style={{ borderColor: 'var(--border)', minHeight: 88 }}>
        <div className="flex items-center mb-2 gap-2">
          {drill && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2.5 text-xs shrink-0 font-medium"
              onClick={onClearDrill}
            >
              <span className="mr-1 text-base leading-none">‹</span> Back
            </Button>
          )}
          {drill ? (
            <div className="truncate" style={{ fontSize: 12, color: 'var(--muted-foreground)', fontWeight: 500 }}>
              {drill.path}
            </div>
          ) : (
            <div className="text-sm font-semibold truncate" style={{ color: 'var(--foreground)' }}>
              Product groups
            </div>
          )}
        </div>
        <Input
          type="text"
          placeholder="Search product group..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {drill && (
        <ParentRow
          name={drill.name}
          share={drill.parent_share}
          revenue={drill.scope_revenue}
          currency={currency}
        />
      )}

      <div className="flex-1 overflow-auto">
        {list.length === 0 ? (
          <div className="p-4 text-sm" style={{ color: 'var(--muted-foreground)' }}>
            No results.
          </div>
        ) : (
          <div className="grid" style={{ gridTemplateColumns: GRID_TEMPLATE }}>
            <HeaderCell>Product group</HeaderCell>
            <HeaderCell align="right">Mix</HeaderCell>
            <HeaderCell align="center">Action</HeaderCell>

            {list.map((entry) => (
              <EntryCells
                key={entry.product_node_id}
                entry={entry}
                added={addedKeys.has(entry.product_node_id)}
                currency={currency}
                onAdd={onAdd}
              />
            ))}

            {!drill && !querying && onAddNewProduct && (
              <NewProductRow
                added={newProductAdded}
                onAdd={() => !newProductAdded && onAddNewProduct()}
              />
            )}

            {!querying && onAddSpecial && (
              <OtherRow
                added={specialAdded}
                disabled={!!drill && items.filter((e) => !addedKeys.has(e.product_node_id)).length <= 1}
                onAdd={() => !specialAdded && onAddSpecial()}
              />
            )}
          </div>
        )}
      </div>

      <div
        className="px-3 py-2 border-t text-xs flex items-center justify-between"
        style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)' }}
      >
        <span>{drill ? `${drill.name} revenue in period` : 'Total revenue in period'}</span>
        <span className="tabular-nums" title={formatRevenueFull(drill ? drill.scope_revenue : data.total_revenue, currency)}>
          {formatRevenueFull(drill ? drill.scope_revenue : data.total_revenue, currency)}
        </span>
      </div>
    </div>
  )
}
